import { setProgressBarCurrentStep } from '@avalara/bat-apps-shared/client/actions/progress-bar';
import { isMobileDevice } from '@avalara/bat-apps-shared/helpers/index';
import sessionStorage from '@avalara/bat-apps-shared/helpers/sessionStorage';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { Pages } from '@avalara/types/offers';
import conf from '../conf';
import DefaultPaymentView from '../containers/default-payment-view';
import IframePaymentContainer from '../containers/iframe-payment-container';
import { handleCybersourcePostMessage } from '../lib/cybersource-message-handler';
import { updateProductAnalytics } from '../lib/side-effects/analytics';
import { getCybersourceFormData } from '../lib/side-effects/cybersource';
import { getSystemErrorMessaging } from '../lib/side-effects/error';
import { addPaidAvataxTier, addReturnsOffer, addRiskAssessmentOffer } from '../lib/side-effects/offers';
import { stepNames } from '../lib/side-effects/progress-bar';
import { selectIsNonAdmin, selectState } from '../selectors';
import { setCurrentOfferPage } from '../store/offer';
import { clearSystemError, setNeedToFetchCybersourceData } from '../store/system';
import OfferPaymentView from './offer/forms/payment';
import { FormContainer } from './offer/styled/forms';
import RiskAssessmentPaymentView from './risk-assessment/payment';
import { RelativeDiv } from './styled/skylab/page';

import type { CybersourceFormData } from '@avalara/types/cybersource';
import type { InitialAddObligationsOptions } from '@avalara/types/index';
import { addCreditCard } from '../lib/actions';
import { EXISTING_PAYMENT_METHOD_TOKEN } from '../lib/constants';
import { createAvataxPaidAccount, createNetNewSTRAAccount } from '../lib/side-effects/accounts';

type Props = {
  paymentView?: string;
  isEmbedded?: boolean;
  onSuccess?: () => void;
};

const PaymentFormContainer = styled(FormContainer)`
  height: inherit;
`;

const defaultFormData: CybersourceFormData = {
  access_key: '',
  amount: '',
  currency: '',
  locale: '',
  profile_id: '',
  reference_number: '',
  signature: '',
  signed_date_time: '',
  signed_field_names: '',
  transaction_type: '',
  transaction_uuid: '',
  unsigned_field_names: '',
  bill_to_forename: '',
  bill_to_surname: '',
  bill_to_address_country: '',
  bill_to_address_line1: '',
  bill_to_address_postal_code: '',
  bill_to_address_city: '',
  bill_to_address_state: '',
  bill_to_company_name: '',
};
const formDataFields = Object.keys(defaultFormData) as Array<keyof CybersourceFormData>;

const CybersourcePaymentPage = ({ paymentView, isEmbedded, onSuccess }: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const hiddenForm = useRef<HTMLFormElement>(null);
  const {
    account: { selectedPaymentMethod, isLoggedIn },
    buyFlow: { hideIframe: hideBuyFlowIframe },
    features,
    offer: { offerFlow },
    sharedSignup: { signupForm },
    system: { activeFlow, needToFetchCybersourceData },
  } = useSelector(selectState);

  const [formData, setFormData] = useState(defaultFormData);
  const isNonAdmin = useSelector(selectIsNonAdmin);
  const isHighRadiusEnabled = !!features.ENABLEHIGHRADIUSMIGRATION;
  const usingExistingPaymentMethod = !!selectedPaymentMethod;
  const hideIframe = usingExistingPaymentMethod || (isLoggedIn && isNonAdmin) || hideBuyFlowIframe;

  useEffect(() => {
    dispatch(setCurrentOfferPage(Pages.Payment));
    dispatch(setProgressBarCurrentStep(stepNames.paymentMethod));
    dispatch(updateProductAnalytics('purchase_start', true));
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: leaving as is
  useEffect(() => {
    if (usingExistingPaymentMethod || !needToFetchCybersourceData) return;
    if (isLoggedIn && isHighRadiusEnabled) return;
    // NOTE(Sasha): If the state is not 2 letter abbrev or company name is missing
    // from signup form, this call will cause iframe to not render or will open the
    // iframe in a new window
    dispatch(getCybersourceFormData('USD', signupForm, setFormData));
    dispatch(setNeedToFetchCybersourceData(false));
  }, [needToFetchCybersourceData]);

  useEffect(() => {
    // NOTE(Sasha): Need to store activeFlow, since state get cleared out in the callback
    if (activeFlow !== null) sessionStorage.set('activeFlow', activeFlow);
  }, [activeFlow]);

  const onAddObligation = (options: InitialAddObligationsOptions) => {
    const retrievedActiveFlow = sessionStorage.get('activeFlow');
    switch (retrievedActiveFlow) {
      case 'avatax-buy':
        dispatch(addPaidAvataxTier(options));
        break;
      case 'avatax-direct-buy':
        dispatch(createAvataxPaidAccount(options));
        break;
      case 'returns':
        dispatch(addReturnsOffer(options));
        break;
      case 'risk-assessment-net-new':
        dispatch(createNetNewSTRAAccount(options));
        break;
      case 'risk-assessment':
        dispatch(addRiskAssessmentOffer(options));
        break;
      default:
        break;
    }
  };

  const onAddCreditCard = (paymentMethodToken: string) => dispatch(addCreditCard(paymentMethodToken));
  const onHandlePaymentError = (message?: string) =>
    dispatch(
      getSystemErrorMessaging({
        errorCode: 'failedCybersourcePayment',
        msg: message || '',
      }),
    );

  // biome-ignore lint/correctness/useExhaustiveDependencies: listener registered once on mount
  useEffect(() => {
    const callbackListener = (msg: MessageEvent) => {
      handleCybersourcePostMessage(msg, {
        dispatch,
        history,
        isLoggedIn,
        isHighRadiusEnabled,
        offerFlow,
        paymentView,
        isEmbedded,
        onSuccess,
        onAddObligation,
        onAddCreditCard,
        onHandlePaymentError,
      });
    };

    window.addEventListener('message', callbackListener);
    return () => window.removeEventListener('message', callbackListener);
  }, []);

  useEffect(() => {
    if (formData.signature) hiddenForm.current?.submit();
  }, [formData.signature]);

  const cybersourcePageEl = document.getElementById('cybersource-page') as HTMLIFrameElement;
  const paymentFormContainerEl = document.getElementById('payment-form-container');
  if (!isMobileDevice() && cybersourcePageEl && paymentFormContainerEl) {
    // NOTE(Sasha): Numbers chosen arbitrarily to adjust inner iframe. Change as necessary
    cybersourcePageEl.width = `${paymentFormContainerEl.offsetWidth - 90}`;
  }

  const onSubmitWithExistingPayment = () => {
    dispatch(clearSystemError());
    onAddObligation({ paymentToken: EXISTING_PAYMENT_METHOD_TOKEN, history });
  };

  const getPaymentView = () => {
    switch (paymentView) {
      case 'offer':
        return <OfferPaymentView isEmbedded={isEmbedded} />;
      case 'risk-assessment-net-new':
      case 'risk-assessment':
        return <RiskAssessmentPaymentView />;
      case 'add-credit-card':
        return (
          <RelativeDiv>
            <IframePaymentContainer hideIframe={hideIframe} isCybersource={true} />
          </RelativeDiv>
        );
      default:
        if (isLoggedIn && isHighRadiusEnabled) {
          return <DefaultPaymentView isHighRadius={true} onHighRadiusSubmit={onSubmitWithExistingPayment} />;
        }
        return <DefaultPaymentView />;
    }
  };

  return (
    <>
      <form
        ref={hiddenForm}
        method='post'
        action={`${conf.cybersourcePageHost}/token/create`}
        target='cybersource-page'
      >
        {formDataFields.map((formDataField) => (
          <input
            type='hidden'
            key={formDataField}
            id={formDataField}
            name={formDataField}
            defaultValue={formData[formDataField]}
          />
        ))}
      </form>

      <PaymentFormContainer id='payment-form-container'>{getPaymentView()}</PaymentFormContainer>
    </>
  );
};

export default CybersourcePaymentPage;
